import React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import { Avatar, Card, Grid, Typography } from "@mui/material";
import { IToDo } from "../../Api/Interface/api.interface";
import { useGetConversationsNotificationQuery } from "../../Api/attoDeskApi";
import { styled, useTheme } from "@mui/material/styles";
import Badge from "@mui/material/Badge";
import { useNavigate } from "react-router-dom";
import GroupIcon from "@mui/icons-material/Group";
import { useStyles } from "../../theme/appStyles";

interface IProfileProps {
  notification: HTMLElement | null;
  onClose: () => void;
  open: boolean;
  notificationData: IToDo[]
}

const Profile: React.FC<IProfileProps> = ({ notification, onClose, open, notificationData }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const classes = useStyles();


  return (
    <Menu
      anchorEl={notification}
      id="account-menu"
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 4,
        sx: {
          borderRadius: 3,
          minWidth: 320,
          overflow: "visible",
          backgroundColor: theme.palette.background.paper,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          mt: 2,
          "& .MuiAvatar-root": {
            width: 36,
            height: 36,
            fontSize: 18,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 18,
            width: 12,
            height: 12,
            bgcolor: theme.palette.background.paper,
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 1,
            boxShadow: "-1px -1px 1px rgba(0,0,0,0.05)",
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      <Typography variant="h6" sx={{ px: 2.5, pt: 2.5, fontWeight: 700 }}>
        🔔 Notifications
      </Typography>

      <Divider sx={{ my: 1 }} />

      <Grid container spacing={1} sx={{ px: 2, pb: 2 }}>
        {notificationData.length === 0 ? (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              py={2}
            >
              No unread conversations.
            </Typography>
          </Grid>
        ) : (
          notificationData.map((group: IToDo) => (
            <Grid item xs={12} key={group.id}>
              <Card
                onClick={() => {
                  navigate(`/conversations/messages/${group.id}`);
                  onClose();
                }}
                elevation={0}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  borderRadius: 2,
                  cursor: "pointer",
                  transition: "background 0.2s",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <Badge
                  badgeContent={
                    group.unread_count > 0 ? group.unread_count : null
                  }
                  color="primary"
                  sx={{
                    mr: 2,
                    "& .MuiBadge-badge": {
                      fontSize: "0.75rem",
                      minWidth: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: "green",
                      color: "#fff",
                    },
                  }}
                >
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    {group.subject ? (
                      group.subject.charAt(0).toUpperCase()
                    ) : (
                      <GroupIcon />
                    )}
                  </Avatar>
                </Badge>

                <Grid container direction="column">
                  <Typography variant="subtitle1" fontWeight={600} noWrap>
                    {group.subject || "Unnamed Group"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={group.unread_count > 0 ? "green" : "text.secondary"}
                    fontWeight={group.unread_count > 0 ? 600 : 400}
                  >
                    {group.unread_count > 0
                      ? `You have ${group.unread_count} unread ${
                          group.unread_count === 1 ? "message" : "messages"
                        }`
                      : "No new messages"}
                  </Typography>
                </Grid>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Menu>
  );
};

export default Profile;
